import { TemplateFormat } from '@langchain/core/prompts';
import { Extension } from '@tiptap/core';
import { EditorState, Plugin, TextSelection } from '@tiptap/pm/state';

// Helper to safely read characters around the cursor.
const charAt = (pos: number, state: EditorState) =>
  pos < 0 || pos >= state.doc.content.size
    ? ''
    : state.doc.textBetween(pos, pos + 1, undefined, '\u0000');

const FORMAT_BEHAVIOR: Record<
  TemplateFormat,
  {
    // return true if we should replace the user input
    shouldHandle: (state: EditorState, from: number, to: number) => boolean;
    // return the literal text to insert, given the variable name
    buildInsert: (varName: string) => string;
  }
> = {
  'f-string': {
    shouldHandle: (state, _from, to) => charAt(to, state) !== '}',
    // The insert here is {var1} because we're replacing the open { user has typed too.
    buildInsert: (name) => `{${name}}`,
  },
  mustache: {
    shouldHandle: (state, from, to) => {
      // When using Mustache `{{ }}` variables, we only want to auto-insert
      // the closing braces when the user has just typed a SINGLE opening brace.
      // The conditions below ensure that:
      // 1. `prev`       – the character immediately before the cursor – is `{`.
      // 2. `prevPrev`   – the character two positions back – is NOT `{`, so we're
      //    not dealing with a double-opening brace already (`{{`).
      // 3. `nextTwo`    – up to the next two characters after the cursor – do NOT
      //    start with `}}`, meaning closing braces haven't already been inserted.
      // Only when all three checks pass do we proceed with auto-insertion.
      const prev = charAt(from - 1, state);
      const prevPrev = charAt(from - 2, state);
      const docSize = state.doc.content.size;
      const endPos = Math.min(to + 2, docSize);
      const nextTwo = state.doc.textBetween(to, endPos, undefined, '\u0000');
      return prev === '{' && prevPrev !== '{' && !nextTwo.startsWith('}}');
    },
    // The insert here has one { because we're inserting on the second open bracket {. So the insert is {var1}}
    buildInsert: (name) => `{${name}}}`,
  },
};
/**
 * Factory to create an Auto-close-variable extension that respects the
 * selected template format.
 *
 *  – For `f-string` templates we auto-close a single brace: `{}`.
 *  – For `mustache` templates we wait for a double-opening brace `{{` and
 *    then insert `}}` so the result is `{{}}`.
 */
export function AutoCloseVariableExtension(templateFormat: TemplateFormat) {
  return Extension.create({
    name: 'autoCloseVariable',

    addStorage() {
      return {
        /** Current count of variables present in the template */
        variables: [] as string[],
        /** Whether we should highlight the variable name after auto-insert */
        shouldHighlightAfterInsert: true as boolean,
      };
    },

    addProseMirrorPlugins() {
      const { storage } = this;
      return [
        new Plugin({
          props: {
            handleTextInput(view, from, to, text) {
              // We only care about the `{` character.
              if (text !== '{') return false;

              const { state } = view;

              const getNextVariableName = () => {
                // Extract numeric indices from existing variables and find the max
                const maxIndex = storage.variables.reduce((max, varName) => {
                  const match = varName.match(/^var(\d+)$/);
                  if (match) {
                    const index = parseInt(match[1], 10);
                    return Math.max(max, index);
                  }
                  return max;
                }, 0);

                const nextIndex = maxIndex + 1;
                const nextVarName = `var${nextIndex}`;

                // Update storage count for subsequent insertions
                storage.variables = [...storage.variables, nextVarName];

                return nextVarName;
              };

              const behavior = FORMAT_BEHAVIOR[templateFormat];
              if (behavior && behavior.shouldHandle(state, from, to)) {
                const variableName = getNextVariableName();
                const insertText = behavior.buildInsert(variableName);
                const tr = state.tr.insertText(insertText, from, to);

                // Highlight the variable name so the user can immediately rename it
                // if the calling code wants that behaviour.
                if (storage.shouldHighlightAfterInsert) {
                  tr.setSelection(
                    TextSelection.create(
                      tr.doc,
                      from + 1,
                      from + 1 + variableName.length
                    )
                  );
                }
                view.dispatch(tr);

                if (!storage.shouldHighlightAfterInsert) {
                  // Insert a zero-width space **after** the newly-inserted variable and move the
                  // cursor so it sits right after that space.
                  const spacePos = from + insertText.length; // end of the variable text
                  const rightSpaceTr = view.state.tr.insertText(
                    '\u2060',
                    spacePos
                  );
                  rightSpaceTr.setSelection(
                    TextSelection.create(rightSpaceTr.doc, spacePos)
                  );
                  view.dispatch(rightSpaceTr);
                }

                return true;
              }

              return false;
            },
          },
        }),
      ];
    },

    addCommands() {
      return {
        /**
         * Update the current variable count from outside the editor.
         */
        updateVariables: (variables: string[]) => () => {
          this.storage.variables = variables;

          return true;
        },
        setShouldHighlightAfterInsert: (shouldHighlight: boolean) => () => {
          this.storage.shouldHighlightAfterInsert = shouldHighlight;
          return true;
        },
      };
    },
  });
}
